import React, { useEffect, useRef } from 'react';
import { ICampaignDeliverables } from '../../../interfaces/campaign.interface';
import { DeliverableTask, Platform, getAssetTypeForPlatform } from '../../../constants/campaign';
import { getNumberOfDeliverables } from '../../../utils/campaignUtils';
import { displayErrorToast } from '../../../utils/ToastUtils';
import { Button } from 'react-bootstrap';
import SubDeliverable from './SubDeliverable';
import Deliverable from './Deliverable';

interface IProps {
    deliverables: ICampaignDeliverables[];
    setDeliverables: React.Dispatch<React.SetStateAction<ICampaignDeliverables[]>>;
}

const DeliverableSection = ({ deliverables, setDeliverables }: IProps) => {
    const lastDeliverableRef = useRef<HTMLDivElement>(null);
    const wasAddedRef = useRef(false);

    const updateDeliverable = (index: number, deliverable: ICampaignDeliverables) => {
        const updatedDeliverables = [...deliverables];
        updatedDeliverables[index] = deliverable;
        setDeliverables(updatedDeliverables);
    };

    const addSubDeliverable = (deliverableNumber: number, isResize: boolean) => {
        setDeliverables((cDeliverables) => {
            let numberOfSubDeliverables = 0;
            let lastSubDeliverableIndex = 0;
            let deliverableIndex = 0;
            let indexToInsertSubDeliverable = 0;
            const currentDeliverables = [...cDeliverables];

            for (let i = 0; i < currentDeliverables.length; i++) {
                if (currentDeliverables[i].deliverable_number === deliverableNumber) {
                    if (currentDeliverables[i].sub_deliverable) {
                        numberOfSubDeliverables++;
                        lastSubDeliverableIndex = i;
                    } else {
                        deliverableIndex = i;
                    }
                }
            }
            const newSubDeliverable: ICampaignDeliverables = {
                asset_type: isResize ? getAssetTypeForPlatform(Platform.INSTAGRAM)[0] : undefined,
                platform: isResize ? Platform.INSTAGRAM : undefined,
                content: '',
                deliverable_number: deliverableNumber,
                sub_deliverable: numberOfSubDeliverables + 1,
                task: isResize ? DeliverableTask.RESIZE : DeliverableTask.VARIANT,
                tags: [],
                templateDetails: null,
                user_product_path: '',
            };
            if (!isResize) newSubDeliverable.variant_count = 2;
            if (numberOfSubDeliverables === 0) {
                indexToInsertSubDeliverable = deliverableIndex;
            } else {
                indexToInsertSubDeliverable = lastSubDeliverableIndex;
            }
            const updatedDeliverables = [
                ...currentDeliverables.slice(0, indexToInsertSubDeliverable + 1),
                newSubDeliverable,
                ...currentDeliverables.slice(indexToInsertSubDeliverable + 1),
            ];

            return updatedDeliverables;
        });
    };

    const deleteSubDeliverable = (deliverableNumber: number, subDeliverableNumber: number) => {
        setDeliverables((currentDeliverables) => {
            let updatedDeliverables = currentDeliverables.filter(
                (d) =>
                    !(
                        d.deliverable_number === deliverableNumber &&
                        d.sub_deliverable === subDeliverableNumber
                    ),
            );

            updatedDeliverables = updatedDeliverables.map((d) => {
                if (
                    d.deliverable_number === deliverableNumber &&
                    d.sub_deliverable &&
                    d.sub_deliverable > subDeliverableNumber
                ) {
                    return {
                        ...d,
                        sub_deliverable: d.sub_deliverable - 1,
                    };
                }
                return d;
            });

            const indexToRemove = currentDeliverables.findIndex(
                (d) =>
                    d.deliverable_number === deliverableNumber &&
                    d.sub_deliverable === subDeliverableNumber,
            );

            return updatedDeliverables;
        });
    };

    const addDeliverables = () => {
        const numberOfDeliverables = getNumberOfDeliverables([...deliverables]);

        if (numberOfDeliverables >= 12) {
            displayErrorToast('You cannot add more than 12 deliverables in total');
            return;
        }
        const newDeliverable: ICampaignDeliverables = {
            asset_type: getAssetTypeForPlatform(Platform.INSTAGRAM)[0],
            platform: Platform.INSTAGRAM,
            content: '',
            deliverable_number: numberOfDeliverables + 1,
            task: DeliverableTask.CREATE_NEW_AD,
            tags: [],
            user_product_path: '',
            templateDetails: null,
        };
        setDeliverables((currentDeliverables) => {
            wasAddedRef.current = true;
            return [...currentDeliverables, newDeliverable];
        });
    };

    const deleteDeliverables = (deliverableNumber: number) => {
        setDeliverables((currentDeliverables) => {
            let updatedDeliverables = currentDeliverables.filter(
                (d) => d.deliverable_number !== deliverableNumber,
            );

            updatedDeliverables = updatedDeliverables.map((d) => {
                if (d.deliverable_number > deliverableNumber) {
                    return {
                        ...d,
                        deliverable_number: d.deliverable_number - 1,
                    };
                }
                return d;
            });

            const indicesToRemove = currentDeliverables
                .map((d, index) => (d.deliverable_number === deliverableNumber ? index : -1))
                .filter((index) => index !== -1);

            return updatedDeliverables;
        });
    };

    useEffect(() => {
        if (wasAddedRef.current && lastDeliverableRef.current) {
            lastDeliverableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            wasAddedRef.current = false;
        }
    }, [deliverables]);

    return (
        <div className="tw-w-full tw-mt-4 tw-rounded-sm tw-py-3 tw-px-4 tw-border tw-border-blue-50 tw-shadow-sm">
            <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <i className="ph-bold ph-image-square lg"></i>
                    <span className="tw-text-base">Deliverables</span>
                </div>
                <Button variant="outline-primary" onClick={addDeliverables}>
                    Add Deliverable
                </Button>
            </div>
            {deliverables.length > 0 && (
                <div className="tw-w-fit tw-my-6 tw-flex tw-items-center tw-gap-x-[6px] tw-py-1 tw-px-2 tw-text-black tw-bg-gray-300 tw-rounded-md tw-font-normal">
                    <i className="ph-bold ph-info lg"></i>
                    <div>
                        The images you see in the deliverables are template previews, which will be
                        brandified in your next step
                    </div>
                </div>
            )}
            {deliverables.length === 0 && (
                <div className="tw-w-fit tw-my-6 tw-flex tw-items-center tw-gap-x-[6px] tw-py-1 tw-px-2 tw-text-black tw-bg-gray-300 tw-rounded-md tw-font-normal">
                    <i className="ph-bold ph-info lg"></i>
                    <div>Start by adding deliverable for your campaign</div>
                </div>
            )}
            {deliverables.length > 0 && (
                <div className="tw-w-full tw-flex tw-flex-col tw-gap-y-4">
                    <>
                        {deliverables.map((deliverable, index) => {
                            const isLast = index === deliverables.length - 1;
                            return (
                                <div
                                    className="tw-w-full"
                                    key={index}
                                    ref={isLast ? lastDeliverableRef : null}
                                >
                                    {deliverable.sub_deliverable ? (
                                        <SubDeliverable
                                            index={index}
                                            deliverable={deliverable}
                                            deleteSubDeliverable={deleteSubDeliverable}
                                            updateDeliverable={updateDeliverable}
                                        />
                                    ) : (
                                        <Deliverable
                                            index={index}
                                            deliverable={deliverable}
                                            deliverables={deliverables}
                                            updateDeliverable={updateDeliverable}
                                            addSubDeliverable={addSubDeliverable}
                                            deleteDeliverables={deleteDeliverables}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </>
                </div>
            )}
        </div>
    );
};

export default DeliverableSection;
